import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Loader } from '@googlemaps/js-api-loader';
import { MarkerClusterer, GridAlgorithm } from '@googlemaps/markerclusterer';
import Window from '@lib/window-size';
import { shortPriceFormatter, formatNumber, toKebabCase } from '@lib/utils';
import ReactDOMServer from 'react-dom/server';
import Listing from '@lib/models/listing';
import Drawer from './drawer/component';
import officeList from '@src/data/office-list.json';
import { graphql } from '@lib/api';
import { gql } from '@apollo/client';

// ---------------------------------------------------------

import { NEARBY_LISTING_QUERY, NEARBY_SOLD_LISTING_QUERY } from './map-listing-queries';

// ---------------------------------------------------------

import styles from './styles.module.scss';
const {
  container,
  map_container,
  drawer,
  drawer_btn,
  drawer_btn_svg,
  drawer_is_open,
  map_info_window,
  similar_properties_label,
  similar_properties_label_with_alert,
  map_info_window_additional_info,
  map_info_window_price,
  map_info_window_office_name,
  property_alert_pending,
  property_alert_new_price,
  property_alert_open_house,
  zoom_control,
  zoom_control_in,
  zoom_control_out,
  maptype_control,
  maptype_control_map,
  maptype_control_satellite,
  maptype_control_is_active,
  recenter_control,
  cluster_label
} = styles;

// ---------------------------------------------------------

// https://developers.google.com/maps/documentation/javascript/marker-clustering
// https://developers.google.com/maps/documentation/javascript/style-reference
// https://developers.google.com/maps/documentation/javascript/shapes
// https://developers.google.com/maps/documentation/javascript/drawinglayer

// ---------------------------------------------------------

import Modal from '@components/modal';
import Icon from '@components/icon';

// ---------------------------------------------------------

const Map = (props) => {
  const { latitude, longitude, address, propertyTypeId, listingId } = props;

  const zoomControl = useRef(null);
  const zoomControlIn = useRef(null);
  const zoomControlOut = useRef(null);
  const mapTypeControl = useRef(null);
  const mapTypeControlMap = useRef(null);
  const mapTypeControlSatellite = useRef(null);
  const recenterControl = useRef(null);

  const isMobile = Window().width <= 720;
  const [centerPosition, setCenterPosition] = useState({
    lat: latitude,
    lng: longitude
  });
  const [drawerIsOpen, setDrawerIsOpen] = useState(true);
  const [mobileDrawerIsOpen, setMobileDrawerIsOpen] = useState(false);
  const [displayOption, setDisplayOption] = useState('currentProperty');
  const [zoomLevel, setZoomLevel] = useState(14);
  const [mapType, setMapType] = useState('roadmap');
  const [poiOptions, setPoiOptions] = useState({
    offices: true,
    poi: true
  });

  const [nearbyProperties, setNearbyProperties] = useState([]);
  const [nearbySoldProperties, setNearbySoldProperties] = useState([]);

  const [markerCount, setMarkerCount] = useState(0);

  const loader = new Loader({
    apiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
    version: 'weekly'
  });

  let mapOptions = {
    center: centerPosition,
    disableDefaultUI: true,
    draggable: true,
    gestureHandling: 'greedy',
    mapTypeId: mapType,
    styles: [
      {
        featureType: 'poi',
        stylers: [{ visibility: poiOptions.poi ? 'on' : 'off' }] // Turn off points of interest.
      }
    ],
    zoom: zoomLevel,
    zoomControl: false
  };

  useEffect(() => {
    const ThirtyDaysAgoDate = new Date(new Date().setDate(new Date().getDate() - 30)).toISOString();

    // 0.116 longitude is ~10 miles
    // 0.09 latitude is ~10 miles
    const variables = {
      currListingId: listingId,
      gteLatitudeValue: latitude - 0.09,
      gteLongitudeValue: longitude - 0.116,
      lteLatitudeValue: latitude + 0.09,
      lteLongitudeValue: longitude + 0.116,
      propertyTypeIdValue: propertyTypeId,
      sellingDateValue: ThirtyDaysAgoDate
    };

    const fetchNearbyData = async () => {
      let data;
      try {
        data = await graphql(
          gql`
            ${NEARBY_LISTING_QUERY}
          `,
          variables
        );
      } catch (err) {
        console.error('ERROR', err);
      }

      setNearbyProperties(data?.listingSearch?.items ?? []);
    };

    const fetchNearbySoldData = async () => {
      let data;
      try {
        data = await graphql(
          gql`
            ${NEARBY_SOLD_LISTING_QUERY}
          `,
          variables
        );
      } catch (err) {
        console.error('ERROR', err);
      }

      setNearbySoldProperties(data?.listingSearch?.items ?? []);
    };

    fetchNearbyData();
    fetchNearbySoldData();

    loader
      .load()
      .then((google) => {
        const map = new google.maps.Map(document.getElementById('map'), mapOptions);

        const mainMarker = new google.maps.Marker({
          heading: address,
          icon: {
            url: '/images/map/current-property-marker.png'
          },
          map,
          position: {
            lat: latitude,
            lng: longitude
          },
          title: address,
          zIndex: 3
        });
        mainMarker.setMap(map);

        const infoWindow = new google.maps.InfoWindow({ disableAutoPan: true, maxWidth: 275 });

        // Nearby Properties
        const nearbyType =
          displayOption === 'nearbyProperties' ? nearbyProperties : nearbySoldProperties;

        if (displayOption !== 'currentProperty') {
          const rectangle = new google.maps.Rectangle({
            bounds: {
              east: longitude + 0.116,
              north: latitude + 0.09,
              south: latitude - 0.09,
              west: longitude - 0.116
            },
            fillOpacity: 0,
            map,
            strokeColor: '#a00715',
            strokeOpacity: 0.8,
            strokeWeight: 2
          });
          rectangle.setMap(map);

          setMarkerCount(nearbyType.length);
          const markers = nearbyType?.map((property) => {
            const isPending = property.alerts?.filter((alert) => alert.type === 'Pending');
            const isOpenHouse = property.openHouses[0];
            const isNewPrice = property.alerts?.filter((alert) => alert.type === 'PriceChange');
            const isSold = property.statusId === 6;

            const setMarkerIcon = () => {
              if (isPending?.length > 0) {
                return '/images/map/pending-marker.png';
              } else if (isOpenHouse) {
                return '/images/map/open-house-marker.png';
              } else if (isNewPrice?.length > 0) {
                return '/images/map/new-price-marker.png';
              } else if (isSold) {
                return '/images/map/sold-marker.png';
              } else {
                return '/images/map/default-marker.png';
              }
            };

            const setMarkerAlertLabel = () => {
              if (isPending?.length > 0) {
                return <div className={property_alert_pending}>{isPending[0].text}</div>;
              } else if (isOpenHouse) {
                return <div className={property_alert_open_house}>Open House Scheduled</div>;
              } else if (isNewPrice?.length > 0) {
                return <div className={property_alert_new_price}>{isNewPrice[0].text}</div>;
              } else {
                return false;
              }
            };

            const marker = new google.maps.Marker({
              icon: {
                url: setMarkerIcon()
              },
              label: {
                className:
                  setMarkerIcon().includes('default') || setMarkerIcon().includes('sold')
                    ? similar_properties_label
                    : similar_properties_label_with_alert,
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold',
                text: shortPriceFormatter(property.price)
              },
              map,

              position: {
                lat: property.latitude,
                lng: property.longitude
              },
              url: Listing.canonicalize(
                `/listing/${property.listingId}/${property.address}-${property.city}-${property.state}-${property.zip}`
              ),
              zIndex: 2
            });

            const propertyInfoWindowCard = (
              <a
                href={Listing.canonicalize(
                  `/listing/${property.listingId}/${property.address}-${property.city}-${property.state}-${property.zip}`
                )}
                target="_blank"
                rel="noreferrer"
                className={map_info_window}
              >
                <div>
                  <img src={property.mainPhoto.midSizeImageUrl} alt={property.address} />
                </div>
                <div>
                  <p>{property.address}</p>
                  <p className={map_info_window_price}>{property.priceFormatted}</p>
                  <div className={map_info_window_additional_info}>
                    {!!property.fullBathroomCount && !!property.partBathroomCount ? (
                      <span>
                        {property.fullBathroomCount}/{property.partBathroomCount} BA
                      </span>
                    ) : property.bathroomCount ? (
                      <span>{property.bathroomCount} BA</span>
                    ) : (
                      ''
                    )}
                    {!!property.bedroomCount && <span>{property.bedroomCount} BR</span>}

                    {!!property.squareFootage && !!property.squareFootage !== '0' && (
                      <span>{formatNumber(property.squareFootage)} Sq. Ft</span>
                    )}
                  </div>
                </div>
                {setMarkerAlertLabel()}
              </a>
            );

            if (isMobile) {
              marker.addListener('click', () => {
                infoWindow.close();
                const content = ReactDOMServer.renderToString(propertyInfoWindowCard);
                infoWindow.setContent(content);
                infoWindow.open(marker.getMap(), marker);
              });
            } else {
              marker.addListener('mouseover', () => {
                infoWindow.close();
                const content = ReactDOMServer.renderToString(propertyInfoWindowCard);
                infoWindow.setContent(content);
                infoWindow.open(marker.getMap(), marker);
              });

              marker.addListener('mouseout', () => {
                infoWindow.close();
              });

              marker.addListener('click', () => {
                window.open(marker.url, '_blank');
              });
            }

            return marker;
          });

          const renderer = {
            render: ({ count, position }) =>
              new google.maps.Marker({
                icon: {
                  url:
                    displayOption === 'nearbySoldProperties'
                      ? '/images/map/sold-marker-cluster.png'
                      : '/images/map/marker-cluster.png'
                },
                label: {
                  className: cluster_label,
                  color: 'white',
                  fontSize: '14px',
                  text: String(count)
                },
                position,
                zIndex: 1
              })
          };

          const algorithm = new GridAlgorithm({ gridSize: 60 });

          new MarkerClusterer({
            algorithm: algorithm,
            map: map,
            markers: markers,
            renderer: renderer
          });
        }

        // Offices
        if (officeList && poiOptions.offices) {
          officeList?.forEach((office) => {
            const marker = new google.maps.Marker({
              icon: {
                url: '/images/map/office-marker.png'
              },
              map,
              optimized: false,
              position: {
                lat: office.latitude,
                lng: office.longitude
              },
              zIndex: 1
            });
            marker.setMap(map);
            const officeInfoWindowCard = (
              <a
                href={`/office/${encodeURIComponent(toKebabCase(office.name))}`}
                className={map_info_window}
              >
                <div>
                  <img src={office.photoUrl} alt={office.name} />
                </div>
                <div>
                  <span className={map_info_window_office_name}>{office.name} Office</span>
                </div>
              </a>
            );

            marker.addListener('click', () => {
              infoWindow.close();
              const content = ReactDOMServer.renderToString(officeInfoWindowCard);
              infoWindow.setContent(content);
              infoWindow.open(marker.getMap(), marker);
            });
          });
        }

        // Map controls
        zoomControlIn.current.onclick = () => {
          map.setZoom(map.getZoom() + 1);
          setZoomLevel(zoomLevel + 1);
        };

        zoomControlOut.current.onclick = () => {
          map.setZoom(map.getZoom() - 1);
          setZoomLevel(zoomLevel - 1);
        };

        mapTypeControlMap.current.onclick = () => {
          setMapType('roadmap');
          map.setMapTypeId('roadmap');
        };

        mapTypeControlSatellite.current.onclick = () => {
          setMapType('hybrid');
          map.setMapTypeId('hybrid');
        };

        recenterControl.current.onclick = () => {
          setCenterPosition({
            lat: latitude,
            lng: longitude
          });
          map.panTo({
            lat: latitude,
            lng: longitude
          });
        };

        map.addListener('dragend', () => {
          setCenterPosition(map.getCenter());
        });
      })
      .catch((err) => {
        console.error(err);
      });
  }, [poiOptions, displayOption]);

  const handleDisplayOptionUpdate = (displayOption) => {
    setCenterPosition({
      lat: latitude,
      lng: longitude
    });

    if (displayOption !== 'currentProperty') {
      setZoomLevel(12);
    } else {
      setZoomLevel(14);
    }

    setDisplayOption(displayOption);
  };

  const drawerContent = (
    <Drawer
      isOpen={drawerIsOpen}
      mapOptionsCallback={setPoiOptions}
      markerOptionsCallback={handleDisplayOptionUpdate}
      markerCount={displayOption !== 'currentProperty' && markerCount}
      mobileModalisOpen={setMobileDrawerIsOpen}
      initialMarkerOptions={displayOption}
      initialMapOptions={poiOptions}
    />
  );

  return (
    <div
      className={classNames(container, {
        [drawer_is_open]: drawerIsOpen
      })}
    >
      {isMobile && (
        <button className={drawer_btn} onClick={() => setMobileDrawerIsOpen(!mobileDrawerIsOpen)}>
          Filters
        </button>
      )}

      {!isMobile && (
        <button className={drawer_btn} onClick={() => setDrawerIsOpen(!drawerIsOpen)}>
          <Icon className={drawer_btn_svg} type="arrow" name="default-left" />
        </button>
      )}

      <div className={zoom_control} ref={zoomControl}>
        <button className={zoom_control_in} title="Zoom In" ref={zoomControlIn}>
          +
        </button>
        <button className={zoom_control_out} title="Zoom Out" ref={zoomControlOut}>
          −
        </button>
      </div>

      <button className={recenter_control} title="Re-Center" ref={recenterControl}>
        <Icon type="map" name="recenter" />
      </button>

      <div className={maptype_control} ref={mapTypeControl}>
        <button
          className={classNames(maptype_control_map, {
            [maptype_control_is_active]: mapType === 'roadmap'
          })}
          title="Show road map"
          ref={mapTypeControlMap}
        >
          Map
        </button>
        <button
          className={classNames(maptype_control_satellite, {
            [maptype_control_is_active]: mapType === 'hybrid'
          })}
          title="Show satellite imagery"
          ref={mapTypeControlSatellite}
        >
          Satellite
        </button>
      </div>
      <div id="map" className={map_container}></div>

      {!isMobile && <div className={drawer}>{drawerContent}</div>}

      {isMobile && (
        <Modal
          modalIsOpen={mobileDrawerIsOpen}
          onModalClose={() => setMobileDrawerIsOpen(!mobileDrawerIsOpen)}
          content={mobileDrawerIsOpen && drawerContent}
          theme="no-padding"
        />
      )}
    </div>
  );
};

Map.propTypes = {
  /**
   * Specifies the address for the main marker
   */
  address: PropTypes.string,

  /**
   * Specifies the latitude for the main marker
   */
  latitude: PropTypes.number,

  /**
   * Specifies the listingId for the main marker
   */
  listingId: PropTypes.number,

  /**
   * Specifies the longitude for the main marker
   */
  longitude: PropTypes.number,

  /**
   * Specifies the property type id.
   */
  propertyTypeId: PropTypes.number
};

export default Map;
